import { Box, CardContent, IconButton, Typography } from "@mui/material";
import Card from "@mui/material/Card";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen"; // Optional: nicer icon for closing
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useEffect, useRef, useState } from "react";
import ThreeViewer from "./threeViewer.component";

/**
 * Questo componente permette di visualizzare un modello 3D all'interno di una Card e interagire, si può fare zoom in, zoom out,
 * si può avere una finestra più grande e si può ruotare liberamente.
 * @returns una Card che contiene il componente ThreeViewer e vari componenti per l'interazione col modello 3D
 */
export default function ThreeDCard({
  modelUrl,
  modelStatus,
  modelTimestamp,
  modelUpdatedHoursAgo,
  modelPayloadFormat,
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const controlsRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {}, [isFullscreen]);

  const zoomIn = () => {
    controlsRef.current?.zoomIn?.();
  };

  const zoomOut = () => {
    controlsRef.current?.zoomOut?.();
  };

  // 1. Listen for native browser fullscreen changes (handles 'Esc' key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    // Cleanup listener on unmount
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        await containerRef.current.requestFullscreen();
      }
    } else {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    }
    // Note: We don't manually setIsFullscreen here anymore because
    // the useEffect above handles it automatically and more reliably.
  };

  const formattedModelTimestamp = modelTimestamp
    ? new Intl.DateTimeFormat("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(modelTimestamp))
    : null;

  const modelStatusLabel = (() => {
    if (modelStatus === "ready") return "GLTF compatibile";
    if (modelStatus === "loading") return "Caricamento modello";
    if (modelStatus === "incompatible") {
      return modelPayloadFormat
        ? `Formato ${modelPayloadFormat.toUpperCase()} non compatibile`
        : "Formato non compatibile";
    }
    if (modelStatus === "error") {
      return modelPayloadFormat
        ? `Formato ${modelPayloadFormat.toUpperCase()} non compatibile`
        : "Modello non disponibile";
    }
    if (modelStatus === "missing") return "Nessun 3D Mesh";
    return "In attesa";
  })();

  return (
    <Card
      variant="outlined"
      ref={containerRef}
      sx={{
        ml: isFullscreen ? 0 : 2, // Remove margins in fullscreen
        mr: isFullscreen ? 0 : 2,
        mt: isFullscreen ? 0 : 1,
        mb: isFullscreen ? 0 : 1,
        backgroundColor: "background.tertiary",
        // Ensure the card takes full space when fullscreen
        height: isFullscreen ? "100vh" : "auto",
        width: isFullscreen ? "100vw" : "auto",
        overflow: "hidden", // Prevent scrollbars
        borderRadius: isFullscreen ? 0 : undefined,
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: isFullscreen ? 2 : undefined,
        }}
      >
        {/* Top Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {formattedModelTimestamp ? `${formattedModelTimestamp}` : ""}
            </Typography>
          </Box>
          <IconButton onClick={toggleFullscreen}>
            {/* Toggle Icon based on state */}
            {isFullscreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
        </Box>

        {/* 2. Viewer Container - Dynamic Height */}
        <Box
          sx={{
            width: "100%",
            // If fullscreen, take remaining height minus headers/footers.
            // If not, use fixed 400px.
            height: isFullscreen ? "calc(100vh - 120px)" : 400,
            display: "flex",
            flex: 1, // Also helps fill space in flex container
            position: "relative", // Good practice for 3D containers
          }}
        >
          {modelUrl ? (
            <ThreeViewer
              controlsRef={controlsRef}
              isFullscreen={isFullscreen}
              modelUrl={modelUrl}
            />
          ) : (
            <Box
              sx={{
                height: "20vh",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ textAlign: "center" }}
                color="text.secondary"
              >
                {modelStatusLabel.toLocaleUpperCase()}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bottom Bar */}
        {!isFullscreen && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            {/* You might want to allow zooming in fullscreen too, so I removed the !isFullscreen check logic, but you can keep it if preferred */}
            <IconButton onClick={zoomIn}>
              <RemoveIcon />
            </IconButton>
            <IconButton onClick={zoomOut}>
              <AddIcon />
            </IconButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
